import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useWindowDimensions, ViewStyle } from 'react-native'
import { CopilotProps, CopilotProvider, useCopilot } from 'react-native-copilot'
import { useTheme } from '@ui-kitten/components'
import { NORA_HOME_WALKTHROUGH_COPILOT_BACKDROP_COLOR } from './constants'
import { NoraHomeCopilotControls } from './NoraHomeCopilotControls'
import { NoraHomeCopilotTooltip } from './NoraHomeCopilotTooltip'
import { getAnchoredStepByTargetId, getStepById } from './steps'
import {
  NoraHomeWalkthroughPresentation,
  NoraHomeWalkthroughProviderProps,
  NoraHomeWalkthroughStepId,
  NoraHomeWalkthroughTargetId,
} from './types'

type NoraHomeWalkthroughContextValue = {
  activeStepId: NoraHomeWalkthroughStepId
  close: () => void
  complete: () => void
  showStep: (stepId: NoraHomeWalkthroughStepId) => void
  showFinalStep: (stopCopilot: () => Promise<void>) => Promise<void>
}

const NoraHomeWalkthroughContext = createContext<NoraHomeWalkthroughContextValue | undefined>(
  undefined,
)

export const useNoraHomeWalkthroughContext = () => useContext(NoraHomeWalkthroughContext)

const EmptyStepNumber = () => null

export const NoraHomeWalkthroughProvider = ({
  children,
  onClose,
  onComplete,
}: NoraHomeWalkthroughProviderProps) => {
  const theme = useTheme()
  const windowDimensions = useWindowDimensions()
  const copilotTooltipStyle: ViewStyle = useMemo(
    () => ({
      backgroundColor: 'transparent',
      borderRadius: 24,
      left: 0,
      maxWidth: windowDimensions.width,
      overflow: 'visible',
      paddingHorizontal: 0,
      paddingTop: 0,
      right: 0,
      width: windowDimensions.width,
    }),
    [windowDimensions.width],
  )
  const [activeStepId, setActiveStepId] = useState<NoraHomeWalkthroughStepId>(
    NoraHomeWalkthroughStepId.Introduction,
  )
  const shouldCloseOnCopilotStopRef = useRef(true)
  const copilotStartedRef = useRef(false)

  const close = useCallback(() => {
    copilotStartedRef.current = false
    setActiveStepId(NoraHomeWalkthroughStepId.Introduction)
    onClose()
  }, [onClose])

  const complete = useCallback(() => {
    copilotStartedRef.current = false
    setActiveStepId(NoraHomeWalkthroughStepId.Introduction)
    onComplete()
  }, [onComplete])

  const showStep = useCallback((stepId: NoraHomeWalkthroughStepId) => {
    const step = getStepById(stepId)
    copilotStartedRef.current = step.presentation === NoraHomeWalkthroughPresentation.Anchored
    setActiveStepId(stepId)
  }, [])

  const showFinalStep = useCallback(async (stopCopilot: () => Promise<void>) => {
    shouldCloseOnCopilotStopRef.current = false
    try {
      await stopCopilot()
      copilotStartedRef.current = false
      setActiveStepId(NoraHomeWalkthroughStepId.Capabilities)
    } finally {
      shouldCloseOnCopilotStopRef.current = true
    }
  }, [])

  const value = useMemo(
    () => ({
      activeStepId,
      close,
      complete,
      showStep,
      showFinalStep,
    }),
    [activeStepId, close, complete, showFinalStep, showStep],
  )

  const handleCopilotStop = useCallback(() => {
    const activeStep = getStepById(activeStepId)

    if (!copilotStartedRef.current || !shouldCloseOnCopilotStopRef.current) {
      return
    }

    copilotStartedRef.current = false

    if (activeStep.presentation !== NoraHomeWalkthroughPresentation.Anchored) {
      return
    }

    close()
  }, [activeStepId, close])

  const handleStepChange = useCallback((targetId?: NoraHomeWalkthroughTargetId) => {
    if (!targetId) {
      return
    }

    const step = getAnchoredStepByTargetId(targetId)

    if (step) {
      setActiveStepId(step.id)
    }
  }, [])

  return (
    <NoraHomeWalkthroughContext.Provider value={value}>
      <CopilotProvider
        animated
        overlay="svg"
        arrowColor={theme['theme.background']}
        arrowSize={0}
        backdropColor={NORA_HOME_WALKTHROUGH_COPILOT_BACKDROP_COLOR}
        margin={10}
        overlayControlsComponent={NoraHomeCopilotControls}
        svgMaskPath={noraHomeCopilotSvgMaskPath}
        tooltipComponent={NoraHomeCopilotTooltip}
        tooltipStyle={copilotTooltipStyle}
        stepNumberComponent={EmptyStepNumber}
        labels={{ next: 'Next', finish: 'Next', skip: 'Skip for now' }}
      >
        <NoraHomeCopilotEvents onStop={handleCopilotStop} onStepChange={handleStepChange} />
        {children}
      </CopilotProvider>
    </NoraHomeWalkthroughContext.Provider>
  )
}

const NoraHomeCopilotEvents = ({
  onStop,
  onStepChange,
}: {
  onStop: () => void
  onStepChange: (targetId?: NoraHomeWalkthroughTargetId) => void
}) => {
  const { copilotEvents } = useCopilot()

  useEffect(() => {
    const handleStepChange = (step?: { name: string }) => {
      onStepChange(step?.name as NoraHomeWalkthroughTargetId | undefined)
    }

    copilotEvents.on('stop', onStop)
    copilotEvents.on('stepChange', handleStepChange)

    return () => {
      copilotEvents.off('stop', onStop)
      copilotEvents.off('stepChange', handleStepChange)
    }
  }, [copilotEvents, onStepChange, onStop])

  return null
}

const animatedValue = (value: unknown) => {
  const maybeAnimatedValue = value as { _value?: number; __getValue?: () => number }

  return maybeAnimatedValue._value ?? maybeAnimatedValue.__getValue?.() ?? 0
}

const MASK_BORDER_RADIUS = 12
const ASK_NORA_MASK_BORDER_RADIUS = 16
// Copilot expands measured target bounds by 2 pixels on each side.
const COPILOT_TARGET_PADDING = 2
const RECTANGULAR_MASK_HORIZONTAL_PADDING = 8
const SETTINGS_MASK_RADIUS = 20

const shouldPadRectangularMask = (stepName: string) =>
  stepName === NoraHomeWalkthroughTargetId.QuickPromptsHeader ||
  stepName === NoraHomeWalkthroughTargetId.ConversationsHeader

const roundedRectPath = (
  x: number,
  y: number,
  width: number,
  height: number,
  borderRadius = MASK_BORDER_RADIUS,
) => {
  const right = x + width
  const bottom = y + height
  const radius = Math.min(borderRadius, width / 2, height / 2)

  return [
    `M${x + radius},${y}`,
    `H${right - radius}`,
    `A${radius},${radius} 0 0 1 ${right},${y + radius}`,
    `V${bottom - radius}`,
    `A${radius},${radius} 0 0 1 ${right - radius},${bottom}`,
    `H${x + radius}`,
    `A${radius},${radius} 0 0 1 ${x},${bottom - radius}`,
    `V${y + radius}`,
    `A${radius},${radius} 0 0 1 ${x + radius},${y}`,
    'Z',
  ].join('')
}

const circlePath = (x: number, y: number, width: number, height: number, radius: number) => {
  const centerX = x + width / 2
  const centerY = y + height / 2

  return [
    `M${centerX},${centerY - radius}`,
    `A${radius},${radius} 0 0 1 ${centerX + radius},${centerY}`,
    `A${radius},${radius} 0 0 1 ${centerX},${centerY + radius}`,
    `A${radius},${radius} 0 0 1 ${centerX - radius},${centerY}`,
    `A${radius},${radius} 0 0 1 ${centerX},${centerY - radius}`,
    'Z',
  ].join('')
}

const noraHomeCopilotSvgMaskPath: NonNullable<CopilotProps['svgMaskPath']> = ({
  canvasSize,
  position,
  size,
  step,
}) => {
  const fullScreenPath = `M0,0H${canvasSize.x}V${canvasSize.y}H0V0Z`
  const stepName = (step as { name?: string } | undefined)?.name

  if (!stepName) {
    return fullScreenPath
  }

  const positionX = animatedValue(position.x)
  const positionY = animatedValue(position.y)
  const sizeX = animatedValue(size.x)
  const sizeY = animatedValue(size.y)
  const horizontalPadding = shouldPadRectangularMask(stepName)
    ? RECTANGULAR_MASK_HORIZONTAL_PADDING
    : 0

  if (stepName === NoraHomeWalkthroughTargetId.AskNoraInput) {
    return `${fullScreenPath}${roundedRectPath(
      positionX + COPILOT_TARGET_PADDING,
      positionY + COPILOT_TARGET_PADDING,
      Math.max(0, sizeX - COPILOT_TARGET_PADDING * 2),
      Math.max(0, sizeY - COPILOT_TARGET_PADDING * 2),
      ASK_NORA_MASK_BORDER_RADIUS,
    )}`
  }

  const targetPath =
    stepName === NoraHomeWalkthroughTargetId.Settings
      ? circlePath(positionX, positionY, sizeX, sizeY, SETTINGS_MASK_RADIUS)
      : roundedRectPath(
          positionX - horizontalPadding,
          positionY,
          sizeX + horizontalPadding * 2,
          sizeY,
        )

  return `${fullScreenPath}${targetPath}`
}
